import { io } from "socket.io-client";

/**
 * WebSocket (Socket.IO) connection setup and management
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.listeners = {};
    this.userId = null;
  }

  /**
   * Connect to the Socket.IO server
   * @param {string} userId - User ID for authentication
   * @returns {Promise} Promise that resolves when connected
   */
  connect(userId) {
    return new Promise((resolve, reject) => {
      this.userId = userId;

      const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY);

      if (!token) {
        return reject(new Error('No authentication token found'));
      }

      if (this.socket) {
        this.socket.disconnect();
      }

      this.socket = io(process.env.REACT_APP_WEBSOCKET_URL || `http://localhost:6120`, {
        auth: { token },
        withCredentials: true,
        autoConnect: true,
      });

      // Connection established
      this.socket.on('connect', () => {
        console.log('Socket.IO connection established');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        resolve();

        // Optionally emit auth message
        this.sendMessage({
          type: 'auth',
          userId,
        });
      });

      // Message received
      this.socket.on('message', (data) => {
        this.handleMessage(data);
      });

      // Custom type-based messages (optional, if backend emits different types)
      this.socket.onAny((event, data) => {
        this.handleMessage({ ...data, type: event });
      });

      // Connection closed
      this.socket.on('disconnect', () => {
        console.log('Socket.IO disconnected');
        this.isConnected = false;
        this.attemptReconnect();
      });

      // Connection error
      this.socket.on('connect_error', (error) => {
        console.error('Socket.IO connect error:', error);
        reject(error);
      });
    });
  }

  /**
   * Attempt to reconnect to the Socket.IO server
   */
  attemptReconnect() {

    return;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Maximum reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`Attempting to reconnect in ${delay / 1000} seconds...`);

    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      if (this.userId) {
        this.connect(this.userId).catch(() => {
          this.attemptReconnect();
        });
      }
    }, delay);
  }

  /**
   * Disconnect from the server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }

    this.isConnected = false;
    this.userId = null;
    clearTimeout(this.reconnectTimeout);
  }

  /**
   * Send a message through Socket.IO
   * @param {Object} data - Message data
   * @returns {boolean} Success status
   */
  sendMessage(data) {
    if (!this.isConnected) {
      console.error('Cannot send message: Socket.IO not connected');
      return false;
    }

    try {
      // You can emit to a specific event, or a generic one
      // this.socket.emit('message', data);
      this.socket.emit(data.type, data);
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  /**
   * Handle incoming Socket.IO messages
   * @param {Object} data - Message data
   */
  handleMessage(data) {
    const eventType = data.type || 'message';

    if (this.listeners[eventType]) {
      this.listeners[eventType].forEach((callback) => {
        callback(data);
      });
    }
  }

  /**
   * Add an event listener for a specific message type
   * @param {string} eventType - Event type to listen for
   * @param {Function} callback - Callback function
   */
  on(eventType, callback) {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = [];
    }

    this.listeners[eventType].push(callback);
  }

  /**
   * Remove an event listener
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function to remove
   */
  off(eventType, callback) {
    if (!this.listeners[eventType]) return;

    this.listeners[eventType] = this.listeners[eventType].filter(
      (cb) => cb !== callback
    );
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isSocketConnected() {
    return this.isConnected;
  }
}

// Singleton instance
const socketService = new SocketService();

export default socketService;
